import type { PreparedAudio } from './prepare.js';

/**
 * The client-side quality gate: is there anything on this take at all?
 *
 * Deliberately narrow. The corpus is better served by grading recordings than
 * by refusing them — a clipped bark is still evidence about what the signal
 * was *for*, and a false rejection costs a contribution from someone who was
 * standing there when it happened. So only two things block: a take with no
 * audible content, and one too short to be anything but a mis-tap. Everything
 * else is a warning the contributor can act on and then overrule.
 *
 * Runs on `PreparedAudio`, so one implementation covers both paths into the
 * flow — the microphone and the file picker.
 */

/**
 * Below this peak, across the whole take, there is nothing to analyse.
 *
 * RecordPanel already coaches at -42 dBFS ("very quiet, move closer"). This
 * sits well under that on purpose: the coaching threshold is for a recording
 * that is poor, this one is for a recording that is empty. A take that never
 * crosses it is a muted mic or a pocket, not a quiet animal.
 */
const SILENT_PEAK_DBFS = -50;

/**
 * Under a second is a mis-tap — start and stop in one gesture.
 *
 * `prepareAudio` already refuses anything under 0.5s at decode time; this is
 * the deliberate-intent floor above it. Kept low because real calls are
 * short: a single bark is a few hundred milliseconds, and the take only has
 * to be long enough to contain one.
 */
const MIN_DURATION_MS = 1000;

/** Matches RecordPanel's live meter, so the warning agrees with the bars. */
const CLIP_AMPLITUDE = 0.98;

/** Below this, clipping is incidental; above it, the take is distorted throughout. */
const CLIP_FRACTION_WARN = 0.05;

export type TakeSeverity = 'ok' | 'warn' | 'block';

export interface TakeAssessment {
  severity: TakeSeverity;
  /** Null when `severity` is 'ok'. Written to be read by a contributor, not a developer. */
  message: string | null;
  peakDbfs: number;
  clippedFraction: number;
}

/**
 * Peak level and clipped share of the take, from the same decoded mono samples
 * the spectrogram draws. Full-rate, so both figures are exact rather than
 * estimated from bucketed peaks.
 */
function measure(peaks: Float32Array): { peakDbfs: number; clippedFraction: number } {
  let peak = 0;
  let clipped = 0;

  for (let i = 0; i < peaks.length; i++) {
    const amplitude = Math.abs(peaks[i] ?? 0);
    if (amplitude > peak) peak = amplitude;
    if (amplitude >= CLIP_AMPLITUDE) clipped++;
  }

  return {
    // The epsilon floor keeps digital silence from returning -Infinity.
    peakDbfs: 20 * Math.log10(Math.max(peak, 1e-4)),
    clippedFraction: peaks.length > 0 ? clipped / peaks.length : 0,
  };
}

export function assessTake(audio: PreparedAudio): TakeAssessment {
  const { peakDbfs, clippedFraction } = measure(audio.peaks);

  if (audio.durationMs < MIN_DURATION_MS) {
    return {
      severity: 'block',
      message: 'That take was under a second — just long enough to be an accidental tap.',
      peakDbfs,
      clippedFraction,
    };
  }

  if (peakDbfs < SILENT_PEAK_DBFS) {
    return {
      severity: 'block',
      message: "There's no sound on that take. Check the microphone isn't covered or muted.",
      peakDbfs,
      clippedFraction,
    };
  }

  if (clippedFraction > CLIP_FRACTION_WARN) {
    return {
      severity: 'warn',
      message:
        'This one is loud enough to distort, which makes it harder to analyse later. It still counts — record again from further back if you can.',
      peakDbfs,
      clippedFraction,
    };
  }

  return { severity: 'ok', message: null, peakDbfs, clippedFraction };
}
